import { AlertTriangle, Mic } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BotContainerIcon from '@/assets/bot-container-icon.webp'
import OpacityAnimation from '@/components/animations/OpacityAnimation.tsx'
import RecordingAudio from '@/components/chat/RecordingAudio'
import UserMessage from '@/components/chat/UserMessage'
import { Button, Column, Row, Text } from '@/components/core'
import AvatarCard from '@/components/layout/AvatarCard.tsx'
import RECORDING_TEST_PHRASE from '@/constants/recording-test-phrase.ts'
import { useAuth } from '@/contexts/AuthContext.tsx'
import testSpeechRecognition from '@/features/Onboarding/services/testSpeechRecognition.ts'
import {
  RealtimeMessage,
  useRealtimeChannel
} from '@/hooks/useRealtimeChannel.ts'
import { ROUTES } from '@/types/routes.ts'

const RecordingTest: React.FC = () => {
  const navigate = useNavigate()

  const { realtimeChannel, realtimeToken, configurations } = useAuth()

  const [isRecording, setIsRecording] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isInvalidRecord, setIsInvalidRecord] = useState(false)
  const [recordedAudio, setRecordedAudio] = useState<Blob | undefined>(
    undefined
  )
  const [speechRecognitionResult, setSpeechRecognitionResult] = useState<
    { qualityScore: number } | undefined
  >(undefined)

  const handleAsyncMessage = useCallback((message: RealtimeMessage) => {
    const data = message.data as { error?: string; qualityScore?: number }

    if (data.error) {
      setIsInvalidRecord(true)
      setSpeechRecognitionResult(undefined)
    }
    if (data.qualityScore) {
      setIsInvalidRecord(data.qualityScore < 80)
      setSpeechRecognitionResult({ qualityScore: data.qualityScore })
    }
    setIsValidating(false)
  }, [])

  useRealtimeChannel(realtimeChannel, realtimeToken, handleAsyncMessage)

  const handleSpeechRecognitionTest = useCallback(async (blob: Blob) => {
    try {
      setRecordedAudio(blob)
      setIsValidating(true)
      setIsRecording(false)

      await testSpeechRecognition(blob)
    } catch (error) {
      setIsInvalidRecord(!!error)
    }
  }, [])

  const handleTryAgain = useCallback(() => {
    setIsRecording(false)
    setIsValidating(false)
    setIsInvalidRecord(false)
    setRecordedAudio(undefined)
    setSpeechRecognitionResult(undefined)
  }, [])

  if (isInvalidRecord && !isValidating) {
    return (
      <AvatarCard variant="image" botAvatar={undefined}>
        <Column className="h-full w-full items-center">
          <Column className="relative h-[128px] w-[128px] shrink-0 items-center justify-center rounded-full bg-gray-100">
            <Mic className="h-[48px] w-[48px]" />
            <Column className="absolute right-[0px] top-[0px] h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#FCA6A7]">
              <AlertTriangle color="#CD0101" className="h-[20px] w-[20px]" />
            </Column>
          </Column>
          <div className="h-[24px]" />
          <Text color="600" align="center" size="lg">
            <Text.Translated
              color="600"
              fontWeight="bold"
              className="max-sm:text-xl sm:text-2xl"
              translationKey="hmm_recording_test_error"
            />{' '}
            <Text.Translated translationKey="microphone_not_good_recording_test_error" />
          </Text>
          <div className="h-[24px]" />
          <UserMessage audioBlob={recordedAudio} variant="secondary" />
          <div className="h-[8px]" />
          <Text color="900" size="sm" align="center">
            <Text.Translated translationKey="audio_quality_recording_test_error" />{' '}
            <Text
              size="sm"
              color="none"
              fontWeight="bold"
              className="text-[#CD0101]"
            >
              {speechRecognitionResult?.qualityScore || 0}
            </Text>
            /100
          </Text>
          <div className="h-[32px]" />
          <Text.Translated
            align="center"
            translationKey="microphone_settings_message_recording_test_error"
          />
          <div className="h-[48px]" />
          <Column className="mx-16 w-full max-w-[410px] justify-center">
            <Button.Translated
              onClick={handleTryAgain}
              translationKey="try_again_recording_test_error"
            />
          </Column>
        </Column>
      </AvatarCard>
    )
  }

  if (!isInvalidRecord && !isValidating && !!speechRecognitionResult) {
    return (
      <AvatarCard variant="icon" botAvatar={BotContainerIcon}>
        <Column className="h-full flex-1">
          <Column className="flex-1 items-center space-y-4">
            <Column className="w-full flex-1 items-center space-y-5">
              <Text
                color="600"
                align="center"
                className="max-sm:text-xl sm:text-2xl"
              >
                <Text.Translated
                  color="600"
                  fontWeight="bold"
                  className="max-sm:text-xl sm:text-2xl"
                  translationKey="wonderful_recording_test_done"
                />{' '}
                <Text.Translated
                  color="600"
                  className="max-sm:text-xl sm:text-2xl"
                  translationKey="hearing_you_recording_test_done"
                />
              </Text>
              <Column className="items-center">
                <Text.Translated
                  color="600"
                  align="center"
                  className="max-w-[504px] max-sm:max-w-[280px] max-sm:text-base"
                  translationKey="settings_completed_recording_test_done"
                />
              </Column>
            </Column>
            <Column className="mx-16 w-full max-w-[360px] justify-center">
              <Button.Translated
                onClick={() => navigate(configurations.shouldRecordVideo ? ROUTES.ONBOARDING.VIDEO_TEST : ROUTES.ONBOARDING.TIMER_TIP)}
                translationKey="next_button_recording_test_done"
              />
            </Column>
          </Column>
        </Column>
      </AvatarCard>
    )
  }

  return (
    <AvatarCard variant="icon" botAvatar={BotContainerIcon}>
      <Column className="h-full flex-1">
        <Column className="flex-1 items-center">
          <Column className="w-full flex-1 items-center space-y-5">
            <Text
              color="600"
              align="center"
              className="max-sm:text-xl sm:text-2xl"
            >
              <Text.Translated
                color="600"
                fontWeight="bold"
                className="max-sm:text-xl sm:text-2xl"
                translationKey="great_recording_user_audio"
              />{' '}
              <Text.Translated translationKey="almost_there_recording_user_audio" />
            </Text>
            <Column className="items-center">
              <Text.Translated
                color="600"
                align="center"
                className="max-w-[380px] max-sm:max-w-[280px] max-sm:text-base"
                translationKey="record_audio_instruction_recording_user_audio"
              />
            </Column>
            <Row className="w-full max-w-[380px] items-center justify-center rounded-2xl bg-electric-indigo-100 bg-opacity-50 p-6">
              <Text size="xl" className="italic !text-electric-indigo-900 ">
                {RECORDING_TEST_PHRASE}
              </Text>
            </Row>
          </Column>
          <OpacityAnimation className="flex w-full flex-row items-center justify-center">
            {!isRecording && !isValidating && (
              <Row className="w-full justify-center">
                <Button.Translated
                  translationKey="record_audio_button_recording_user_audio"
                  onClick={() => setIsRecording(true)}
                />
              </Row>
            )}
            {(isRecording || isValidating) && (
              <RecordingAudio
                variant="primary"
                onRecordingEnd={handleSpeechRecognitionTest}
                pending={isValidating}
              />
            )}
          </OpacityAnimation>
        </Column>
      </Column>
    </AvatarCard>
  )
}

export default RecordingTest
